/**
 * Purchase Branch Modal Component (T038, US4, US7)
 *
 * Allows Enterprise tier users to purchase additional branch subscriptions
 * using activation codes from Mayar payment gateway.
 *
 * Features:
 * - Activation code input
 * - Branch name input
 * - Payment link to Mayar
 * - Error handling and validation
 * - Success message and auto-refresh
 */

import { checkFeatureAccess } from '../tier/feature-gate'
import { showUpgradePrompt } from '../tier/upgrade-modal'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/core/firebase'
import { fetchAndCachePlanLimits } from '../store/api'
import { checkAndShowUserAgreement } from '../tier/user-agreement'
import * as $store from '../store/api'

/**
 * Show purchase branch modal
 *
 * Allows Enterprise tier users to purchase additional branches
 */
export async function showPurchaseBranchModal() {
  // --- [NEW] Check for User Agreement FIRST ---
  const agreed = await checkAndShowUserAgreement()
  if (!agreed) return // User cancelled or error
  // --- [END NEW] ---

  const backdrop = document.createElement('div')
  backdrop.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center'

  const modal = document.createElement('div')
  modal.className = 'relative p-5 border w-full max-w-md shadow-lg rounded-md bg-white'

  modal.innerHTML = `
    <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium text-gray-900">Purchase Additional Branch</h3>
        <button id="modal-close-x" class="text-gray-400 hover:text-gray-500">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
        </button>
    </div>
    
    <div class="mb-6">
        <p class="text-sm text-gray-600 mb-4">
            Enter a valid "Branch License" code to add a new branch to your account.
        </p>
        
        <div class="space-y-4">
            <div>
                <label for="activation-code" class="block text-sm font-medium text-gray-700">Branch License Code</label>
                <input type="text" id="activation-code" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="e.g. BRCH-1234-EFGH-5678">
            </div>
             <div>
                <label for="branch-name" class="block text-sm font-medium text-gray-700">New Branch Name</label>
                <input type="text" id="branch-name" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="e.g. Jakarta Pusat">
            </div>
        </div>
        
        <div id="purchase-error" class="mt-2 text-sm text-red-600 hidden"></div>
        <div id="purchase-success" class="mt-2 text-sm text-green-600 hidden"></div>
    </div>

    <div class="flex justify-end gap-3">
        <button id="purchase-cancel" class="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none">
            Cancel
        </button>
        <button id="purchase-apply" class="px-4 py-2 bg-indigo-600 text-white rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none">
            Activate Branch
        </button>
    </div>
  `

  backdrop.appendChild(modal)
  document.body.appendChild(backdrop)

  const closeBtn = modal.querySelector('#modal-close-x')
  const applyBtn = modal.querySelector('#purchase-apply')
  const cancelBtn = modal.querySelector('#purchase-cancel')
  const codeInput = modal.querySelector('#activation-code') as HTMLInputElement
  const nameInput = modal.querySelector('#branch-name') as HTMLInputElement
  const errorDiv = modal.querySelector('#purchase-error') as HTMLDivElement
  const successDiv = modal.querySelector('#purchase-success') as HTMLDivElement

  const closeModal = () => {
    backdrop.remove()
  }

  closeBtn?.addEventListener('click', closeModal)
  cancelBtn?.addEventListener('click', closeModal)

  applyBtn?.addEventListener('click', async () => {
    const activationCode = codeInput.value.trim()
    const branchName = nameInput.value.trim()

    if (!activationCode) {
      errorDiv.textContent = 'Please enter a license code.'
      errorDiv.classList.remove('hidden')
      return
    }

    if (!branchName) {
        errorDiv.textContent = 'Please enter a name for the new branch.'
        errorDiv.classList.remove('hidden')
        return
    }

    errorDiv.classList.add('hidden')
    successDiv.classList.add('hidden')
    
    // Disable UI
    if (applyBtn) (applyBtn as HTMLButtonElement).disabled = true;
    if (applyBtn) applyBtn.textContent = 'Verifying...';
    if (codeInput) codeInput.disabled = true;
    if (nameInput) nameInput.disabled = true;

    try {
      const purchaseBranch = httpsCallable(functions, 'purchaseBranch')
      const result = await purchaseBranch({ activationCode, branchName })
      
      const data = result.data as any
      if (data.success) {
        successDiv.textContent = 'Branch added successfully!'
        successDiv.classList.remove('hidden')
        
        await fetchAndCachePlanLimits()
        
        setTimeout(() => {
            closeModal()
            alert(`Successfully added branch: ${branchName}`)
            window.location.reload()
        }, 1500)
      }
    } catch (error: any) {
      console.error('Purchase failed:', error)
      errorDiv.textContent = error.message || 'Invalid code or server error.'
      errorDiv.classList.remove('hidden')
      
      // Re-enable UI
      if (applyBtn) (applyBtn as HTMLButtonElement).disabled = false;
      if (applyBtn) applyBtn.textContent = 'Activate Branch';
      if (codeInput) codeInput.disabled = false;
      if (nameInput) nameInput.disabled = false;
    }
  })
}